/* eslint-disable camelcase */
import { useForm } from "react-hook-form";
import { defaultValuesTargetingPreset } from "./data";
import { useSegmentData } from "./CreateOrEdit/SubModules/Segment/segment.hook";
import { SUBMODULES, CTV_CHANNELS_KEY } from "./targetingExpression.constant";
import { useGeoData } from "./CreateOrEdit/SubModules/Geo/geo.hook";
import { yupResolver } from "@hookform/resolvers/yup";
import { AnyObjectSchema } from "yup";
import Lazy from "yup/lib/Lazy";
import { useCallback } from "react";
import { useAppSiteData } from "./CreateOrEdit/SubModules/AppSite/appsite.hook";
import { useEnvironmentData } from "./CreateOrEdit/SubModules/Environment/environment.hook";
import { useExchangeData } from "./CreateOrEdit/SubModules/Exchange/exchange.hook";
import { usePlatformData } from "./CreateOrEdit/SubModules/Platform/platform.hook";
import { useQuery } from "@tanstack/react-query";
import { findChannels } from "@/apis/v1/ctv";
import { mapTargetingExpressionContent } from "./targetingExpression.helpers";
import { useCustomSegmentData } from "./CreateOrEdit/SubModules/CustomSegment/customsegment.hook";

export function useCreateOrEditForm<
  ValidationSchema extends AnyObjectSchema | Lazy<any, unknown>
>({
  data,
  isEdit = false,
  validationSchema,
  beeswaxAdvertiserId = null,
}: {
  data?: Record<string, any> | null;
  isEdit?: boolean;
  validationSchema: ValidationSchema;
  beeswaxAdvertiserId: number | null;
}) {
  const defaultValues = useDefaultValues({
    data,
    isEdit,
    beeswaxAdvertiserId,
  });

  const rhForm = useForm<typeof defaultValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  return { rhForm, defaultValues };
}

function useDefaultValues({
  data,
  isEdit,
  beeswaxAdvertiserId,
}: {
  data: Record<string, any> | null | undefined;
  isEdit: boolean;
  beeswaxAdvertiserId: number | null;
}) {
  if (isEdit && data) return defaultValuesForEdit(data);

  return defaultValuesTargetingPreset(beeswaxAdvertiserId);
}

export function defaultValuesForEdit(data: Record<string, any>) {
  const { name, advertiser_id, alternative_id, content, active } = data;

  return {
    name: name ?? "",
    advertiserId: advertiser_id ?? null,
    alternativeId: alternative_id ?? "",
    active: active ? "active" : "inactive",
    content: mapTargetingExpressionContent(content),
  };
}

export function usePresetFilterData({
  data,
  isEdit,
  advertiserId,
}: {
  data: any;
  isEdit: boolean;
  advertiserId?: number;
}) {
  const memoedDataToFetchForEdit = useCallback(dataToFetchForEdit, []);
  const segmentData = useSegmentData({
    data: data?.["user"]?.["segment"],
    isEdit,
  });

  const customSegmentData = useCustomSegmentData({
    advertiserId,
    isEdit,
  });

  const appSiteData = useAppSiteData({
    isEdit,
    areFilters: memoedDataToFetchForEdit(data, "app_site"),
  });

  const geoData = useGeoData({
    isEdit,
    areFilters: memoedDataToFetchForEdit(data, "geo"),
  });

  const environmentData = useEnvironmentData({
    isEdit,
    areFilters: memoedDataToFetchForEdit(data, "environment"),
  });
  const exchangeData = useExchangeData({
    isEdit,
    areFilters: memoedDataToFetchForEdit(data, "exchange"),
  });
  const platformData = usePlatformData({
    isEdit,
    areFilters: memoedDataToFetchForEdit(data, "platform"),
  });
  return {
    [SUBMODULES.SEGMENT]: segmentData,
    [SUBMODULES.GEO]: geoData,
    [SUBMODULES.APP_SITE]: appSiteData,
    [SUBMODULES.CTV_CHANNEL]: appSiteData,
    [SUBMODULES.EXCHANGE]: exchangeData,
    [SUBMODULES.ENVIRONMENT]: environmentData,
    [SUBMODULES.PLATFORM]: platformData,
    [SUBMODULES.TIME]: {},
    [SUBMODULES.CUSTOM_SEGMENT]: customSegmentData,
  };
}

function dataToFetchForEdit(data: Record<string, any>, moduleName: string) {
  const solution: Record<string, boolean> = {};
  const filterKeys = Object.keys(data[moduleName]);
  filterKeys.forEach((filterKey) => {
    if (Array.isArray(data[moduleName][filterKey]["value"])) {
      setKeyValue(
        solution,
        filterKey,
        Boolean(data[moduleName][filterKey]["value"].length)
      );
    }
    if (data[moduleName][filterKey]["value"]?.["excluded"]) {
      setKeyValue(
        solution,
        filterKey,
        Boolean(data[moduleName][filterKey]["value"]["excluded"].length)
      );
    }
    if (data[moduleName][filterKey]["value"]?.["included"]) {
      setKeyValue(
        solution,
        filterKey,
        Boolean(data[moduleName][filterKey]["value"]["included"].length)
      );
    }
  });
  return solution;
}

function setKeyValue(
  object: Record<string, boolean>,
  key: string,
  value: boolean
) {
  if (!object[key]) {
    object[key] = value;
    return;
  }
  if (object[key] === true) return;

  if (object[key] === false && value === true) {
    object[key] = value;
    return;
  }
  return;
}

export function useCTVChannels({
  search,
  lineItemId,
  sortBy,
  savedOnly,
  enabled = true,
  page,
  size,
  onSuccess,
}: {
  search?: string;
  lineItemId?: number;
  sortBy?: string;
  savedOnly?: boolean;
  enabled?: boolean;
  page?: number;
  size?: number;
  onSuccess?: (data: any) => void;
} = {}) {
  return useQuery(
    CTV_CHANNELS_KEY.all(search, lineItemId, sortBy, savedOnly, page, size),
    findChannels,
    {
      meta: {
        queryParams: {
          search,
          lineItemId,
          sortBy,
          savedOnly,
          page,
          size,
        },
      },
      enabled,
      onSuccess,
    }
  );
}
